import React, { FC, ReactNode } from "react";
import classNames from "classnames";
import { SECTION_SCROLL_MARGIN } from "./SectionRail";

/**
 * The card Consumer Voice and Product pages sit in, in every state: the video
 * details page's VideoPanel chrome (VID/panels/VideoPanel.tsx — --surface,
 * hairline, 12px radius, --sh-1, p-4 sm:p-5, a 16px title, 14px to the body),
 * plus what this page needs: an aria-label (sections are landmarks here), the
 * rail's scroll offset, a sub-line under the title, a header action and the
 * page's `hidden` class while a video is open. One chrome for the empty,
 * loading and filled looks, so a section's title never moves between them.
 *
 *   <SectionCard id="product-pages" title="Product pages & audits">
 *     <ScoreState skin="empty" icon={…} title="No product page registered" … />
 *   </SectionCard>
 */

/** The card chrome — shared with the page skeleton's stand-ins, so they can't drift. */
export const SECTION_CARD_CHROME = "bg-surface border border-border-token rounded-xl p-4 sm:p-5";
/** The card's --sh-1 — the header's meta card and the page messages wear it too. */
export const CARD_SHADOW = { boxShadow: "var(--sh-1)" } as const;
/** A section's h2 (the Videos heading too). */
export const SECTION_TITLE_CLASS = "m-0 font-CircularStdBlack text-text-1";
export const SECTION_TITLE_STYLE = { fontSize: 16, lineHeight: 1.2 } as const;

interface Props {
  id: string;
  title: string;
  /** Beside the title (a CountPill). */
  count?: ReactNode;
  /** A 12px --text-2 line under the title ("Analysed for … · date"). */
  subtitle?: ReactNode;
  /** The header's right edge (the "Add a page" pill). */
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

export const SectionCard: FC<Props> = ({ id, title, count, subtitle, action, className, bodyClassName, children }) => (
  <section
    id={id}
    aria-label={title}
    className={classNames(`${SECTION_CARD_CHROME} min-w-0 ${SECTION_SCROLL_MARGIN}`, className)}
    style={CARD_SHADOW}
  >
    <div className="flex flex-wrap items-center gap-x-[9px] gap-y-2">
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-[9px] min-w-0">
          <h2 className={SECTION_TITLE_CLASS} style={SECTION_TITLE_STYLE}>
            {title}
          </h2>
          {count}
        </div>
        {subtitle && (
          <span className="text-text-2" style={{ fontSize: 12 }}>
            {subtitle}
          </span>
        )}
      </div>
      {action && (
        <>
          <span className="flex-1" />
          {action}
        </>
      )}
    </div>
    <div className={classNames("min-w-0", bodyClassName)} style={{ marginTop: 14 }}>
      {children}
    </div>
  </section>
);

/**
 * A section's loading body: the footprint of the ScoreState block it most often
 * becomes (VID/performance/ScoreState.tsx — 12px radius, min-height 176), so
 * the common empty look lands without a jump. A block with a button is taller
 * than the 176 floor: Product pages' "No product page registered" + Add a page
 * is 187 at desktop widths (44 padding, 40 plate, 12 + 17 title, 4 + 18 body,
 * 14 + 36 button, 2 border), so it passes STATE_BLOCK_WITH_ACTION.
 */
export const STATE_BLOCK_WITH_ACTION = 187;

export const StateBlockSkeleton: FC<{ minHeight?: number }> = ({ minHeight = 176 }) => (
  <div
    className="rounded-xl bg-track animate-pulse motion-reduce:animate-none"
    style={{ minHeight }}
    aria-hidden="true"
  />
);

